import { useEffect, useState } from 'react';
import { useSiteContext } from '../context/SiteContext.jsx';
import { getStoredPublishedActiveTemplate, normalizeTemplateForReader } from './TemplateStorage.jsx';
import { getDomainThemeDefaults, normalizeTemplateTheme } from './TemplateTheme.jsx';
import { EDUCATION_TEMPLATE_STYLES, getEducationTemplateStyle } from './EducationTemplateVariant.js';

const EDUCATION_DOMAIN = 'EDUCATION';
const defaultEducationTheme = getDomainThemeDefaults(EDUCATION_DOMAIN);

const pick = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const toCssColor = (value, fallback) => pick(value, fallback);

const normalizeHex = (value) => {
  const color = String(value || '').trim();
  if (!color.startsWith('#')) return null;
  const raw = color.slice(1);
  if (raw.length === 3) {
    return raw.split('').map((char) => char + char).join('');
  }
  if (raw.length === 6) return raw;
  return null;
};

export const educationAlpha = (color, opacity = 0.1) => {
  const hex = normalizeHex(color);
  if (!hex) return `rgba(116, 110, 172, ${opacity})`;

  const intValue = parseInt(hex, 16);
  const red = (intValue >> 16) & 255;
  const green = (intValue >> 8) & 255;
  const blue = intValue & 255;
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const shadeHex = (color, amount = -18) => {
  const hex = normalizeHex(color);
  if (!hex) return color;

  const clamp = (value) => Math.max(0, Math.min(255, value));
  const intValue = parseInt(hex, 16);
  const red = clamp(((intValue >> 16) & 255) + amount);
  const green = clamp(((intValue >> 8) & 255) + amount);
  const blue = clamp((intValue & 255) + amount);
  return `#${((1 << 24) + (red << 16) + (green << 8) + blue).toString(16).slice(1)}`;
};

const activeStoredEducationTemplate = () => {
  return getStoredPublishedActiveTemplate({
    companyId: typeof window !== 'undefined' ? localStorage.getItem('CompPrimaryId') : '',
    domainType: EDUCATION_DOMAIN
  });
};

export const getActiveEducationTemplate = (siteContext = null) => {
  if (siteContext?.templateResolution) {
    if (siteContext.templateResolution.status !== 'assigned' || !siteContext.activeTemplate) return null;
    return normalizeTemplateForReader(siteContext.activeTemplate, EDUCATION_DOMAIN);
  }

  const sourceTemplate = activeStoredEducationTemplate();
  return sourceTemplate ? normalizeTemplateForReader(sourceTemplate, EDUCATION_DOMAIN) : null;
};

export const getActiveEducationTheme = (siteContext = null) => {
  const isContextUnresolved = siteContext?.templateResolution
    && !['assigned', 'default'].includes(siteContext.templateResolution.status);
  const template = getActiveEducationTemplate(siteContext);
  const templateStyle = template
    ? getEducationTemplateStyle(template)
    : isContextUnresolved ? '' : EDUCATION_TEMPLATE_STYLES.DEFAULT;
  const normalizedTheme = template ? normalizeTemplateTheme(template, EDUCATION_DOMAIN) : defaultEducationTheme;
  const primary = toCssColor(normalizedTheme.primary, defaultEducationTheme.primary);
  const secondary = toCssColor(normalizedTheme.secondary, defaultEducationTheme.secondary);
  const accent = toCssColor(normalizedTheme.accent, primary);

  // Default template only: CTA buttons use a single solid black fill (with white
  // text) instead of the two-color primary→secondary gradient. Published/custom
  // templates keep their configured gradient. This is scoped to --education-gradient
  // so it only affects gradient buttons in the education domain, not other domains
  // or non-button accents (icons, borders, rail use --education-primary/-rail-gradient).
  const isDefaultTemplate = !template;
  const buttonGradient = isDefaultTemplate
    ? 'linear-gradient(135deg, #000000 0%, #000000 100%)'
    : `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`;
  const buttonGradientHover = isDefaultTemplate
    ? 'linear-gradient(135deg, #1a1a1a 0%, #1a1a1a 100%)'
    : `linear-gradient(135deg, ${shadeHex(primary, -26)} 0%, ${primary} 100%)`;

  return {
    ...defaultEducationTheme,
    ...normalizedTheme,
    templateStyle,
    isResolved: !siteContext?.templateResolution || ['assigned', 'default'].includes(siteContext.templateResolution.status),
    isExamTemplate: templateStyle === EDUCATION_TEMPLATE_STYLES.EXAM,
    primary,
    primaryColor: primary,
    primaryDark: shadeHex(primary, -26),
    primaryLight: educationAlpha(primary, 0.14),
    secondary,
    secondaryColor: secondary,
    secondaryDark: shadeHex(secondary, -26),
    secondaryLight: educationAlpha(secondary, 0.14),
    accent,
    background: toCssColor(normalizedTheme.background, defaultEducationTheme.background),
    surface: toCssColor(normalizedTheme.surface, defaultEducationTheme.surface),
    text: toCssColor(normalizedTheme.color, defaultEducationTheme.color),
    color: toCssColor(normalizedTheme.color, defaultEducationTheme.color),
    muted: toCssColor(normalizedTheme.mutedColor, defaultEducationTheme.mutedColor),
    mutedColor: toCssColor(normalizedTheme.mutedColor, defaultEducationTheme.mutedColor),
    border: toCssColor(normalizedTheme.borderColor, defaultEducationTheme.borderColor),
    borderColor: toCssColor(normalizedTheme.borderColor, defaultEducationTheme.borderColor),
    fontFamily: normalizedTheme.fontFamily || defaultEducationTheme.fontFamily,
    headingFontFamily: normalizedTheme.headingFontFamily || normalizedTheme.fontFamily || defaultEducationTheme.headingFontFamily,
    buttonRadius: normalizedTheme.buttonRadius || defaultEducationTheme.buttonRadius,
    cardRadius: normalizedTheme.cardRadius || defaultEducationTheme.cardRadius,
    cardShadow: normalizedTheme.cardShadow || defaultEducationTheme.cardShadow,
    inputRadius: normalizedTheme.inputRadius || defaultEducationTheme.inputRadius,
    gradient: buttonGradient,
    gradientHover: buttonGradientHover,
    railGradient: `linear-gradient(90deg, ${primary} 0%, ${secondary} 100%)`
  };
};

export const useActiveEducationTheme = (providedSiteContext = null) => {
  const inheritedSiteContext = useSiteContext();
  const siteContext = providedSiteContext || inheritedSiteContext;
  const [activeTheme, setActiveTheme] = useState(() => getActiveEducationTheme(siteContext));

  useEffect(() => {
    const refresh = () => setActiveTheme(getActiveEducationTheme(siteContext));
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener('avps-template-updated', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('avps-template-updated', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [siteContext]);

  return activeTheme;
};

export const getEducationThemeCssVariables = (theme = getActiveEducationTheme()) => ({
  '--education-primary': theme.primary,
  '--education-primary-dark': theme.primaryDark,
  '--education-primary-faint': educationAlpha(theme.primary, 0.03),
  '--education-primary-subtle': educationAlpha(theme.primary, 0.05),
  '--education-primary-soft-08': educationAlpha(theme.primary, 0.08),
  '--education-primary-soft': educationAlpha(theme.primary, 0.1),
  '--education-primary-soft-15': educationAlpha(theme.primary, 0.15),
  '--education-primary-muted': educationAlpha(theme.primary, 0.2),
  '--education-primary-shadow': educationAlpha(theme.primary, 0.3),
  '--education-primary-glow': educationAlpha(theme.primary, 0.4),
  '--education-secondary': theme.secondary,
  '--education-secondary-dark': theme.secondaryDark,
  '--education-secondary-faint': educationAlpha(theme.secondary, 0.03),
  '--education-secondary-subtle': educationAlpha(theme.secondary, 0.05),
  '--education-secondary-soft': educationAlpha(theme.secondary, 0.1),
  '--education-secondary-muted': educationAlpha(theme.secondary, 0.2),
  '--education-secondary-shadow': educationAlpha(theme.secondary, 0.3),
  '--education-secondary-glow': educationAlpha(theme.secondary, 0.4),
  '--education-accent': theme.accent,
  '--education-background': theme.background,
  '--education-surface': theme.surface,
  '--education-text': theme.color,
  '--education-muted': theme.mutedColor,
  '--education-border': theme.borderColor,
  '--education-font': theme.fontFamily,
  '--education-heading-font': theme.headingFontFamily,
  '--education-button-radius': theme.buttonRadius,
  '--education-card-radius': theme.cardRadius,
  '--education-card-shadow': theme.cardShadow,
  '--education-input-radius': theme.inputRadius,
  '--education-gradient': theme.gradient,
  '--education-gradient-hover': theme.gradientHover,
  '--education-rail-gradient': theme.railGradient,
  '--education-exam-primary': theme.primary,
  '--education-exam-primary-dark': theme.primaryDark,
  '--education-exam-accent': theme.accent,
  '--education-exam-background': theme.background,
  '--education-exam-surface': theme.surface,
  '--education-exam-text': theme.color,
  '--education-exam-muted': theme.mutedColor,
  '--education-exam-border': theme.borderColor,
  '--education-exam-success': theme.successColor || '#16a34a',
  '--education-exam-warning': theme.warningColor || '#f59e0b',
  '--education-exam-error': theme.errorColor || '#dc2626'
});

export const educationButtonSx = (theme, variant = 'primary') => {
  const isSecondary = variant === 'secondary';
  const isOutline = variant === 'outline';
  const color = isSecondary ? theme.secondary : theme.primary;
  const hover = isSecondary ? theme.secondaryDark : theme.primaryDark;

  return {
    borderRadius: theme.buttonRadius || '8px',
    fontFamily: theme.fontFamily,
    fontWeight: 700,
    textTransform: 'none',
    ...(isOutline
      ? {
          color,
          borderColor: color,
          '&:hover': {
            borderColor: hover,
            backgroundColor: educationAlpha(color, 0.1)
          }
        }
      : {
          color: '#ffffff',
          backgroundColor: color,
          '&:hover': {
            backgroundColor: hover
          }
        })
  };
};

export const educationCourseColors = (theme = getActiveEducationTheme()) => ({
  primary: { main: theme.primary, light: theme.primaryLight, dark: theme.primaryDark, contrast: '#FFFFFF' },
  secondary: { main: theme.secondary, light: theme.secondaryLight, dark: theme.secondaryDark, contrast: '#FFFFFF' },
  green: { main: '#04AA6D', light: '#05C380', dark: '#038A57', contrast: '#FFFFFF' },
  status: {
    evaluated: { main: theme.secondary, light: educationAlpha(theme.secondary, 0.12) },
    completed: { main: '#48BB78', light: '#F0FFF4' },
    inProgress: { main: '#ED8936', light: '#FFFAF0' },
    ready: { main: '#ECC94B', light: '#FFFBEB' },
    locked: { main: '#A0AEC0', light: '#F7FAFC' },
    error: { main: '#E53E3E', light: '#FED7D7' },
    success: { main: '#48BB78', light: '#F0FFF4' },
    warning: { main: '#ED8936', light: '#FFFAF0' },
    info: { main: theme.secondary, light: educationAlpha(theme.secondary, 0.12) },
    terminated: { main: '#E53E3E', light: '#FEF2F2' }
  },
  background: { main: '#F8FAFC', paper: '#FFFFFF', gradient: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)' },
  text: { primary: '#1A202C', secondary: '#4A5568', tertiary: '#718096', disabled: '#A0AEC0' },
  border: { light: '#E2E8F0', medium: '#CBD5E0' }
});
